import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Divider,
  message,
  Typography,
  DatePicker,
  Tooltip,
  Checkbox,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Select from 'components/Select';
import NumericInput from 'components/NumericInput';
import {
  generateCURPRules,
  generateEmailRules,
  generateRFCRules,
  generatePhoneRules,
  generateINERules,
  curpsGenericas,
  rfcGenerico,
} from 'utils/rules';
import { getEmpresas } from 'api/empresas/empresas';
import { onError } from 'utils/handlers';
import { getCiudadanoCaja } from 'api/cuentaunicasir/index';
import { getContribuyenteCaja } from 'api/empresas/contribuyentes';
import {
  toUpper,
  capitalize,
  decimalNormalizer,
  toInteger,
  INTEGER_PATTERN,
} from 'utils/normalizers';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { claveHomonima, digitoVerificador, digitoVerificadorRFC } from 'utils/patterns';
import numeroALetras, { romanToInt } from 'utils/numeroALetras';
import { useSelector } from 'react-redux';
import { selectPublicoGeneral } from 'store/reducers/auth';
import LadaNumero from './LadaNumero';
import { InputSearchPro } from './InputSearch';

const REPUVE = process.env.REACT_APP_ENV_NAME === 'repuve';

const FormsAddBusquedaAvanzada = ({
  catalogs,
  tipoDePadron,
  disabled,
  visibleBusquedaAvanzada,
  setVisibleBusquedaAvanzada,
  setLoading,
  empresaFound,
  setEmpresaFound,
  ciudadanoFound,
  setCiudadanoFound,
  contribuyenteFound,
  setContribuyenteFound,
  visibleBusquedaAvanzadaContribuyente,
  setVisibleBusquedaAvanzadaContribuyente,
  visibleBusquedaAvanzadaCiudadano,
  setVisibleBusquedaAvanzadaCiudadano,
  // isBajaDeOtroEstado,
  gpm,
  allowAddAddress,
  datosContactoOpcionales,
}) => {
  const pg = useSelector(selectPublicoGeneral);
  const form = Form.useFormInstance();
  const tiene_estacionamiento = Form.useWatch('tiene_estacionamiento', form);
  const tipo_de_persona = Form.useWatch('tipo_de_persona', form);
  const {
    tiposDeEstablecimiento,
    unidadesRecaudadoras,
    tiposDeVehiculos,
    lineasVehiculares,
    marcasDeVehiculos,
    clasesDeVehiculos,
    serviciosDelVehiculo,
    estadosDelVehiculo,
    tiposDePersonas,
    regimenDeCapital,
    tiposDeEmpresa,
    generos,
    estados,
  } = catalogs;
  const padronSolicitante = gpm && JSON.parse(localStorage.getItem('padronSolicitante'));
  const [autoCompose, setAutoCompose] = useState(false);

  useEffect(() => {
    if (padronSolicitante) {
      (padronSolicitante.tipo_de_padron === 1
        ? setCiudadanoFound : setContribuyenteFound)(padronSolicitante);
      form.setFieldsValue({
        [padronSolicitante.tipo_de_padron === 1
          ? 'ciudadano' : 'contribuyente']: padronSolicitante.id,
      });
    }
    // form "initial values"
    if (tipoDePadron === 2) {
      form.setFieldsValue({ estacionamiento: false });
    }
  }, []);

  const required = {
    required: !(visibleBusquedaAvanzadaCiudadano || visibleBusquedaAvanzadaContribuyente),
  };

  const rules = {
    required: [required],
    razon_social: [
      required,
      {
        validator: async (_, razon_social) => {
          const regimen = [...regimenDeCapital.slice(1), regimenDeCapital[0]]
            .find((r) => razon_social.split(` ${r?.abreviatura}`)[1] === '');
          if (regimen) {
            throw new Error('No incluir abreviatura de régimen capital');
          }
        },
      },
    ],
    sitio_web: [
      {
        type: 'url',
        message: 'Ingrese una URL válida',
      },
    ],
    email: generateEmailRules({
      validateUnique: !visibleBusquedaAvanzada
        && !visibleBusquedaAvanzadaContribuyente
        && !visibleBusquedaAvanzadaCiudadano,
      tipoDePadron,
      required: false,
    }),
    RFC: generateRFCRules({
      validateUnique: !visibleBusquedaAvanzada
        && !visibleBusquedaAvanzadaContribuyente
        && !visibleBusquedaAvanzadaCiudadano,
      tipoDePadron,
      required: false,
      tipoDePersonaName: 'tipo_de_persona',
    }),
    CURP: generateCURPRules({
      validateUnique: !visibleBusquedaAvanzada
        && !visibleBusquedaAvanzadaContribuyente
        && !visibleBusquedaAvanzadaCiudadano,
      tipoDePadron,
      required: false,
    }),
    INE: generateINERules({ validateUnique: true }),
    nombre: [
      {
        validator: async (_, value) => {
          const { apellido_paterno, apellido_materno } = form.getFieldsValue();
          if (value && !apellido_paterno && !apellido_materno) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
    apellido_paterno: [
      {
        validator: async (_, value) => {
          const { nombre, apellido_materno } = form.getFieldsValue();
          if (value && !nombre && !apellido_materno) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
    apellido_materno: [
      {
        validator: async (_, value) => {
          const { apellido_paterno, nombre } = form.getFieldsValue();
          if (value && !apellido_paterno && !nombre) {
            throw new Error('Debe ingresar mínimo dos campos referentes a nombre');
          }
        },
      },
    ],
  };

  const globalPhoneRules = generatePhoneRules({
    tipoDePadron,
    validateUnique: true,
  });

  const phoneRules = {
    telefono_celular: generatePhoneRules({
      tipoDePadron,
      validateUnique: true,
      required: !datosContactoOpcionales,
    }),
    telefono_fijo: globalPhoneRules,
    numero_de_celular: globalPhoneRules,
  };

  const resetAndValidate = () => {
    setEmpresaFound([]);
    setCiudadanoFound([]);
    setContribuyenteFound([]);
    form.resetFields(['q']);
  };

  const searchCiudadano = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _data = await getCiudadanoCaja({ q });
        const filtered = {
          ..._data,
          results: _data.results.filter((e) => e.id !== pg),
        };
        if (filtered.results.length) {
          const [selected] = filtered.results;
          form.setFieldsValue({
            ciudadano: selected.id,
          });
          setCiudadanoFound([selected]);
        } else if (!filtered.results.length) {
          message.info('No se encontró al ciudadano');
          setVisibleBusquedaAvanzadaCiudadano(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const searchContribuyente = async () => {
    try {
      setLoading(true);
      await form.validateFields(['_q']);
      const { _q: q } = form.getFieldsValue(['_q']);
      if (q) {
        const _contribuyenteFound = await getContribuyenteCaja({ q });
        if (_contribuyenteFound.results.length === 1) {
          const [selected] = _contribuyenteFound.results;
          form.setFieldsValue({
            contribuyente: selected.id,
          });
          setContribuyenteFound([selected]);
        } else if (!_contribuyenteFound.results.length) {
          message.info('No se encontró el contribuyente');
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const visibleAddAddress = !visibleBusquedaAvanzadaCiudadano && !visibleBusquedaAvanzada
    && !visibleBusquedaAvanzadaContribuyente && allowAddAddress;

  // Form de Ciudadano
  const CiudadanoForm = () => (
    <Row gutter={10}>
      {visibleBusquedaAvanzadaCiudadano && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            label="Clave"
            name="clave_ciudadana"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      )}
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="nombre"
          rules={rules.required}
          label="Nombre"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_paterno"
          rules={rules.required}
          label="Apellido paterno"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="apellido_materno"
          label="Apellido materno"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="CURP"
          rules={rules.CURP}
          name="CURP"
          normalize={(value) => value.toUpperCase()}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="RFC"
          rules={rules.RFC}
          name="RFC"
          normalize={(val) => val?.toUpperCase()}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="email"
          rules={rules.email}
          label="Correo electrónico"
          normalize={capitalize}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <LadaNumero
        form={form}
        rules={phoneRules}
        tooltip="Número de Celular"
        names={{
          lada: 'lada',
          numero_de_telefono: 'numero_de_celular',
        }}
        celular
        disabled={disabled}
        breakPoints={{
          xs: 24,
          sm: 24,
          md: 16,
        }}
      />
      {visibleAddAddress && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="agregarDireccion"
            label="Agregar dirección"
          >
            <Select trueFalse />
          </Form.Item>
        </Col>
      )}
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Row>
  );

  const EmpresaForm = () => (!visibleBusquedaAvanzadaCiudadano ? (
    <Row gutter={10}>
      {visibleBusquedaAvanzada && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            label="Clave"
            name="clave"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
      )}
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="Razón Social"
          name="razon_social"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="Nombre comercial"
          name="nombre_comercial"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="Página web"
          name="pagina_web"
          normalize={capitalize}
        >
          <Input allowClear disabled={disabled} />
        </Form.Item>
      </Col>
      <Col md={8} sm={24} xs={24}>
        <Form.Item
          label="Tipo de establecimiento"
          name="tipo_de_establecimiento"
        >
          <Select
            dataSource={tiposDeEstablecimiento}
            labelProp="nombre"
            disabled={disabled}
          />
        </Form.Item>
      </Col>
      <LadaNumero
        names={{
          lada: 'lada_celular',
          numero_de_telefono: 'telefono_celular',
        }}
        label="Teléfono Fijo"
        breakPoints={{ md: 8 }}
      />
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="tiene_estacionamiento"
          label="Estacionamiento"
        >
          <Select trueFalse />
        </Form.Item>
      </Col>
      {tiene_estacionamiento && (
        <>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="numero_de_cajones_de_estacionamiento"
              label="No. De Cajones de Estacionamiento"
              normalize={(val) => toInteger(val)}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="estacionamiento_descripcion"
              label="Descripción estacionamiento"
            >
              <Input />
            </Form.Item>
          </Col>
        </>
      )}
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="superficie_construida_del_terreno"
          label="Sup. Construida del Terreno (m2)"
          normalize={(val) => decimalNormalizer(val)}
        >
          <Input />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="tipo_de_empresa"
          label="Tipo de Empresa"
        >
          <Select dataSource={tiposDeEmpresa} />
        </Form.Item>
      </Col>
      <InputSearchPro
        disabled={disabled || padronSolicitante}
        label="Ciudadano"
        name="q"
        onClickAdvanced={() => setVisibleBusquedaAvanzadaCiudadano(true)}
        breakPoints={{
          xs: 24,
          sm: 24,
          md: 12,
          xl: 8,
        }}
        inputProps={{
          onPressEnter: () => searchCiudadano(),
          onChange: ({ target: { value } }) => {
            if (!value) {
              resetAndValidate();
            }
          },
        }}
        resultProps={{
          breakPoints: {
            xs: 24,
            sm: 24,
            md: 12,
            xl: 8,
          },
          keyLabelRender: true,
          name: 'ciudadano',
          keyProp: 'clave_ciudadana',
          label: 'Ciudadano',
          dataSource: ciudadanoFound,
          labelProp: 'nombre_completo',
          onClear: !gpm && resetAndValidate,
        }}
      />
      <InputSearchPro
        disabled={disabled || padronSolicitante}
        label="Contribuyente"
        name="_q"
        onClickAdvanced={() => {
          setVisibleBusquedaAvanzadaContribuyente(true);
          setVisibleBusquedaAvanzadaCiudadano(true);
        }}
        breakPoints={{
          xs: 24,
          sm: 24,
          md: 12,
          xl: 8,
        }}
        inputProps={{
          onPressEnter: () => searchContribuyente(),
          onChange: ({ target: { value } }) => {
            if (!value) {
              resetAndValidate();
            }
          },
        }}
        resultProps={{
          breakPoints: {
            xs: 24,
            sm: 24,
            md: 12,
            xl: 8,
          },
          keyLabelRender: true,
          name: 'contribuyente',
          label: 'Contribuyente',
          dataSource: contribuyenteFound,
          labelProp: 'nombre_completo',
          onClear: !gpm && resetAndValidate,
        }}
      />
      {visibleAddAddress && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="agregarDireccion"
            label="Agregar dirección"
          >
            <Select trueFalse />
          </Form.Item>
        </Col>
      )}
    </Row>
  ) : (<CiudadanoForm />));

  const searchEmpresa = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const { q } = form.getFieldsValue(['q']);
      if (q) {
        const _empresaFound = await getEmpresas({ q });
        if (_empresaFound.results.length) {
          const [selected] = _empresaFound.results;
          form.setFieldsValue({
            empresa: selected.id,
          });
          setEmpresaFound([selected]);
        } else if (!_empresaFound.results.length) {
          message.info('No se encontró la empresa');
          setVisibleBusquedaAvanzada(true);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  // Padrones derivados de Empresa
  const DefaultForm = () => (!visibleBusquedaAvanzada ? (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="descripcion"
          name="Descripción"
          normalize={capitalize}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="RFC"
          rules={rules.RFC}
          name="rfc"
          normalize={toUpper}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="CURP"
          rules={rules.CURP}
          name="curp"
          normalize={toUpper}
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          label="unidad recaudadora"
          name="unidad_recaudadora"
        >
          <Select dataSource={unidadesRecaudadoras} />
        </Form.Item>
      </Col>
      <InputSearchPro
        disabled={disabled}
        label="Empresa"
        name="q"
        onClickAdvanced={() => setVisibleBusquedaAvanzada(true)}
        breakPoints={{
          xs: 24,
          sm: 24,
          md: 16,
          xl: 8,
        }}
        inputProps={{
          onPressEnter: () => searchEmpresa(),
          onChange: ({ target: { value } }) => {
            if (!value) {
              resetAndValidate();
            }
          },
        }}
        resultProps={{
          breakPoints: {
            xs: 24,
            sm: 24,
            md: 16,
            xl: 8,
          },
          keyLabelRender: true,
          name: 'empresa',
          label: 'Empresa',
          dataSource: empresaFound,
          labelProp: 'nombre_comercial',
          onClear: resetAndValidate,
        }}
      />
    </Row>
  ) : (<EmpresaForm />));

  const clavesEstados = ['AS', 'BC', 'BS', 'CC', 'CL', 'CM', 'CS', 'CH', 'DF', 'DG', 'GT', 'GR', 'HG', 'JC', 'MC', 'MN', 'MS', 'NT', 'NL', 'OC', 'PL', 'QT', 'QR', 'SP', 'SL', 'SR', 'TC', 'TS', 'TL', 'VZ', 'YN', 'ZS', 'NE'];
  const ignoreListName = ['JOSE', 'MARIA', 'J', 'J.', 'MA', 'MA.'];
  const ignoreListComSoc = ['COMPAXIA', 'CIA', 'SOCIEDAD', 'SOC'];
  const ignoreListConnectores = ['DA', 'DAS', 'DE', 'DEL', 'DER', 'DI', 'DIE', 'DD', 'EL', 'LA', 'LOS', 'LAS', 'LE', 'LES', 'MAC', 'MC', 'VAN', 'VON', 'Y'];
  const especiales = {
    '@': 'ARROBA',
    '´': 'APOSTROFE',
    '%': 'PORCIENTO',
    '#': 'NUMERO',
    '!': 'ADMIRACION',
    '.': 'PUNTO',
    $: 'PESOS',
    '"': 'COMILLAS',
    '-': 'GUION',
    '/': 'DIAGONAL',
    '+': 'SUMA',
    '(': 'ABRE PARENTESIS',
    ')': 'CIERRA PARENTESIS',
  };

  const regexEspeciales = /[@´%#!."-/+()]/;

  const inconvenientes = ['BUEI', 'BUEY', 'CACA', 'CACO', 'CAGA', 'CAGO', 'CAKA', 'COGE', 'COJA', 'COJE', 'COJI', 'COJO', 'CULO', 'FETO', 'GUEY', 'JOTO', 'KACA', 'KACO', 'KAGA', 'KAGO',
    'KOGE', 'KOJO', 'KAKA', 'KULO', 'MAME', 'MAMO', 'MEAR', 'MEON', 'MION', 'MOCO', 'MULA', 'PEDA', 'PEDO', 'PENE', 'PUTA', 'PUTO', 'QULO', 'RATA', 'RUIN'];

  const composeCURP = () => {
    if (!autoCompose) return;
    const {
      estado_nacimiento,
      genero,
      fecha_nacimiento,
      nombre: _nombre = '',
      apellido_paterno: _apellido_paterno = '',
      apellido_materno: _apellido_materno = '',
      razon_social: _razon_social = '',
    } = form.getFieldsValue();
    let curp;
    let rfc;
    const normalize = (t) => t.replace(/Ñ/g, 'X').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const apellido_paterno_norm = normalize(_apellido_paterno);
    const apellido_materno_norm = normalize(_apellido_materno);
    const razon_social_norm = normalize(_razon_social);
    const nombre_norm = normalize(_nombre);

    if (tipo_de_persona === 1) {
      const nombres = nombre_norm.split(' ').filter((e) => e);
      if (ignoreListName.includes(nombres[0])) delete nombres[0];
      const removeConnectors = (a) => a.filter((e) => !ignoreListConnectores.includes(e))
        .join(' ').replace(/[-\\.\\/´¨]/g, 'X');
      const nombre = removeConnectors(nombres);
      const apellido_paterno = removeConnectors(apellido_paterno_norm.split(' '));
      const apellido_materno = removeConnectors(apellido_materno_norm.split(' '));
      const hasAllInfo = estado_nacimiento && genero && fecha_nacimiento && nombres.length;
      const curp17 = [
        apellido_paterno[0] || 'X',
        apellido_paterno.substr(1).replace(/[^aeiou]/gi, '')[0] || 'X',
        apellido_materno[0] || 'X',
        nombre[0] || 'X',
        fecha_nacimiento?.format('YYMMDD') || '000000',
        { 1: 'H', 2: 'M' }[genero] || 'X',
        clavesEstados[estado_nacimiento - 1] || 'XX',
        apellido_paterno.substr(1).replace(/[aeiou]/gi, '')[0] || 'X',
        apellido_materno.substr(1).replace(/[aeiou]/gi, '')[0] || 'X',
        nombre.substr(1).replace(/[aeiou]/gi, '')[0] || 'X',
        fecha_nacimiento && (parseInt(fecha_nacimiento.format('YYYY'), 10) < 2000 ? '0' : 'A'),
      ].filter((e) => e).join('');
      curp = curp17.replace(/Ñ/g, 'X');
      if (inconvenientes.includes(curp.substring(0, 4))) {
        curp = `${curp.substring(0, 1)}X${curp.substring(2)}`;
      }
      if (hasAllInfo) {
        curp = curp.concat(digitoVerificador(curp));
      }
      rfc = [
        apellido_paterno[0] || 'X',
        !apellido_paterno && 'X',
        apellido_paterno.length > 2 && (apellido_paterno.substr(1).replace(/[^aeiou]/gi, '')[0] || 'X'),
        apellido_materno && apellido_materno[0],
        nombre[0] || 'X',
        (apellido_paterno.length < 3 || !apellido_materno) && nombre[1],
        fecha_nacimiento?.format('YYMMDD') || '000000',
      ].filter((e) => e).join('');
      if (inconvenientes.includes(rfc.substring(0, 4))) {
        rfc = `${rfc.substring(0, 3)}X${rfc.substring(4)}`;
      }
      if (fecha_nacimiento && nombres.length) {
        const homoclave = claveHomonima([nombre, apellido_paterno, apellido_materno].join(' '));
        rfc = rfc.concat(homoclave);
        rfc = rfc.concat(digitoVerificadorRFC(rfc));
      }
    }
    if (tipo_de_persona === 2) {
      const _nombres = razon_social_norm.split(' ')
        .filter((e) => !ignoreListConnectores.includes(e)).map((e) => e.replace(',', '')).map((e) => {
          if (/(?:[A-Z])*[A-Z]\.$/.test(e)) return e.replace(/\./g, ' ').split(' ');
          if (INTEGER_PATTERN.test(e)) return numeroALetras(e).split(' ');
          if (e && /^M{0,4}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/.test(e)) return numeroALetras(romanToInt(e)).split(' ');
          if (especiales[e]) return especiales[e].split(' ');
          if (regexEspeciales.test(e)) return e.replace(RegExp(regexEspeciales, 'g'), '');
          if (ignoreListComSoc.includes(e)) return null;
          return e;
        })
        .flat();
      const nombres = _nombres.join('~')
        .replace(/[-\\.\\/]/g, 'X').split('~')
        .filter((e) => e);
      const hasAllInfo = fecha_nacimiento && nombres.length;
      rfc = [
        nombres[0]?.[0] || '?',
        nombres.length ? nombres[1]?.[0] || nombres[0][1] || 'X' : '?',
        nombres.length ? nombres[2]?.[0] || nombres[1]?.[1] || nombres[0][2] || 'X' : '?',
        fecha_nacimiento?.format('YYMMDD') || '000000',
      ].join('');
      if (hasAllInfo) {
        const homoclave = claveHomonima(razon_social_norm);
        rfc = rfc.concat(homoclave);
        rfc = rfc.concat(digitoVerificadorRFC(rfc));
      }
    }
    rfc = rfc.padEnd(tipo_de_persona === 2 ? 12 : 13, '?');
    form.setFieldsValue({ rfc });
    if (curp) {
      curp = curp.padEnd(18, '?');
      form.setFieldsValue({ curp });
      if (!curp.includes('00') && !curp.includes('?')) form.validateFields(['curp', 'rfc']);
    }
  };

  const onChangeGenero = (_genero) => {
    composeCURP();
    if (tipo_de_persona !== 3) return;
    const { curp } = form.getFieldsValue();
    if (!curp || curp === curpsGenericas[_genero === 2 ? 0 : 1]) {
      form.setFieldsValue({ curp: curpsGenericas[_genero - 1] });
    }
  };

  const onChangeTipoDePersona = (_tipoDePersona) => {
    const { curp, rfc } = form.getFieldsValue();
    if (_tipoDePersona === 3) {
      form.setFieldsValue({
        curp: curp || curpsGenericas[0],
        genero: 1,
        rfc: rfc || rfcGenerico,
      });
    } else {
      if (curpsGenericas.includes(curp)) form.setFieldsValue({ curp: null });
      if (rfc === rfcGenerico) form.setFieldsValue({ rfc: null });
    }
  };

  const getAddonAfter = () => (REPUVE ? (
    <Tooltip
      title={(
        <>
          Autocompletado con datos personales
          {' '}
          <strong>{autoCompose ? 'habilitado' : 'deshabilitado'}</strong>
          {!autoCompose && <br />}
          {!autoCompose && 'Click para habilitar'}
        </>
      )}
    >
      <Checkbox checked={autoCompose} onClick={() => setAutoCompose(!autoCompose)}>
        <QuestionCircleOutlined />
      </Checkbox>
    </Tooltip>
  ) : undefined);

  const tooltip = 'Dato no guardado solo usado para generación de CURP/RFC';

  const ContribuyenteForm = () => (
    <Row gutter={10}>
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="rfc"
          rules={rules.RFC}
          label="RFC"
          normalize={toUpper}
          required={tipo_de_persona === 2}
        >
          <Input
            allowClear
            disabled={disabled}
            maxLength={tipo_de_persona === 2 ? 12 : 13}
            addonAfter={tipo_de_persona !== 3 ? getAddonAfter() : undefined}
          />
        </Form.Item>
      </Col>
      {tipo_de_persona !== 2 && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="curp"
            label="CURP"
            rules={rules.CURP}
            normalize={toUpper}
          >
            <Input allowClear maxLength={18} addonAfter={getAddonAfter()} />
          </Form.Item>
        </Col>
      )}
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="tipo_de_persona"
          rules={rules.required}
          label="Tipo de Persona"
        >
          <Select dataSource={tiposDePersonas} onChange={onChangeTipoDePersona} />
        </Form.Item>
      </Col>
      {tipo_de_persona === 2 ? (
        <>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="razon_social"
              rules={rules.razon_social}
              label="Denominación o Razón Social"
              normalize={toUpper}
            >
              <Input onChange={composeCURP} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Nombre comercial"
              name="nombre_comercial"
              normalize={capitalize}
              rules={REPUVE ? rules.required : []}
            >
              <Input allowClear disabled={disabled} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="regimen_capital"
              label="Régimen Capital o de Capital"
            >
              <Select dataSource={regimenDeCapital} render={(e) => `${e.descripcion} (${e?.abreviatura || ''})`} allowClear />
            </Form.Item>
          </Col>
        </>
      ) : (
        <>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="nombre"
              rules={rules.required}
              label="Nombre"
              normalize={toUpper}
            >
              <Input onChange={composeCURP} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="apellido_paterno"
              rules={rules.required}
              label="Apellido paterno"
              normalize={toUpper}
            >
              <Input onChange={composeCURP} allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="apellido_materno"
              rules={rules.second_last_name}
              label="Apellido materno"
              normalize={toUpper}
            >
              <Input onChange={composeCURP} allowClear />
            </Form.Item>
          </Col>
        </>
      )}
      {(autoCompose && tipo_de_persona !== 2) && (
        <Col md={8} sm={24} xs={24}>
          <Form.Item label="Género" name="genero" tooltip={tooltip}>
            <Select dataSource={generos} onChange={onChangeGenero} />
          </Form.Item>
        </Col>
      )}
      {autoCompose && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item label={tipo_de_persona === 2 ? 'Fecha de Constitución' : 'Fecha de Nacimiento'} name="fecha_nacimiento" tooltip={tooltip}>
            <DatePicker onChange={composeCURP} placeholder="" format={DATE_FRONTEND_FORMAT} />
          </Form.Item>
        </Col>
      )}
      {tipo_de_persona === 1 && autoCompose && (
        <>
          <Col md={8} sm={24} xs={24}>
            <Form.Item label="Estado de nacimiento" name="estado_nacimiento" tooltip={tooltip}>
              <Select
                onChange={composeCURP}
                dataSource={[
                  ...estados,
                  { id: -1, nombre_de_AGEE: 'DOBLE NACIONALIDAD, NACIDO EXTRANJERO O NATURALIZADO' },
                ]}
                labelProp="nombre_de_AGEE"
              />
            </Form.Item>
          </Col>
        </>
      )}
      {tipo_de_persona === 1 && (
        <>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="licencia"
              label="Licencia"
              normalize={toUpper}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              name="pasaporte"
              label="Pasaporte"
              normalize={toUpper}
            >
              <Input allowClear />
            </Form.Item>
          </Col>
        </>
      )}
      <Col span={24} />
      <Typography.Title level={3}>Datos de Contacto</Typography.Title>
      <Col span={24} />
      <LadaNumero
        form={form}
        rules={phoneRules}
        required={false}
        names={{
          lada: 'lada_celular',
          numero_de_telefono: 'telefono_celular',
        }}
        label="Teléfono Celular"
        breakPoints={{
          xxl: 8,
          xl: 8,
          lg: 8,
          md: 8,
          sm: 24,
          xs: 24,
        }}
      />
      <LadaNumero
        form={form}
        rules={phoneRules}
        required={false}
        names={{
          lada: 'lada_fijo',
          numero_de_telefono: 'telefono_fijo',
        }}
        label="Teléfono Fijo"
        breakPoints={{
          xxl: 8,
          xl: 8,
          lg: 8,
          md: 8,
          sm: 24,
          xs: 24,
        }}
      />
      <Col xs={24} sm={24} md={8}>
        <Form.Item
          name="correo_electronico"
          rules={rules.email}
          label="Correo electrónico"
        >
          <Input allowClear />
        </Form.Item>
      </Col>
      {visibleAddAddress && (
        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="agregarDireccion"
            label="Agregar dirección"
          >
            <Select trueFalse />
          </Form.Item>
        </Col>
      )}
    </Row>
  );

  const forms = {
    // ciudadano
    1: (<CiudadanoForm />),
    // Empresa
    2: (<EmpresaForm />),
    // Predio
    3: (
      <Row gutter={10}>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Cuenta única de predial"
            name="cuenta_unica_de_predial"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="CURT"
            name="CURT"
            normalize={(val) => val.toUpperCase()}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="Clave catastral estándar"
            name="clave_catastral_estandar"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col md={6} sm={24} xs={24}>
          <Form.Item
            label="No. Cuenta"
            name="clave_catastral_municipal"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        {!disabled && (
          <>
            <Col span={24}>
              <Divider style={{ marginTop: 0 }}>Por Dirección</Divider>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <NumericInput
                label="Código postal"
                name="direccion__codigo_postal"
              />
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Calle principal"
                normalize={capitalize}
                name="direccion__calle_principal"
              >
                <Input allowClear />
              </Form.Item>
            </Col>
            <Col md={6} sm={24} xs={24}>
              <Form.Item
                label="Número exterior"
                name="direccion__numero_exterior"
                normalize={capitalize}
              >
                <Input allowClear />
              </Form.Item>
            </Col>
          </>
        )}
      </Row>
    ),
    // Vehiculos
    4: (
      <Row gutter={10}>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Número de Placa"
            name="numero_de_placa"
            normalize={capitalize}
          >
            <Input allowClear disabled={disabled} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Tipo de Vehículo"
            name="tipo_de_vehiculo"
          >
            <Select
              dataSource={tiposDeVehiculos}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Línea"
            name="linea"
          >
            <Select
              dataSource={lineasVehiculares}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Modelo"
            name="modelo_del_vehiculo"
          >
            <Select
              dataSource={marcasDeVehiculos}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Clase"
            name="clase_del_vehiculo"
          >
            <Select
              dataSource={clasesDeVehiculos}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Servicio"
            name="servicio"
          >
            <Select
              dataSource={serviciosDelVehiculo}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Form.Item
            label="Estatus"
            name="estatus_del_vehiculo"
          >
            <Select
              dataSource={estadosDelVehiculo}
              render={(e) => e.nombre}
              disabled={disabled}
            />
          </Form.Item>
        </Col>
      </Row>
    ),
    // Contribuyente
    15: (<ContribuyenteForm />),
  };

  return forms?.[tipoDePadron] || <DefaultForm />;
};

FormsAddBusquedaAvanzada.propTypes = {
  // isBajaDeOtroEstado: PropTypes.bool,
  tipoDePadron: PropTypes.number.isRequired,
  catalogs: PropTypes.shape({
    regimenDeCapital: PropTypes.arrayOf(PropTypes.shape({})),
    generos: PropTypes.arrayOf(PropTypes.shape({})),
    estadosCiviles: PropTypes.arrayOf(PropTypes.shape({})),
    tiposDeEstablecimiento: PropTypes.arrayOf(PropTypes.shape({})),
    unidadesRecaudadoras: PropTypes.arrayOf(PropTypes.shape({})),
    tiposDeVehiculos: PropTypes.arrayOf(PropTypes.shape({})),
    lineasVehiculares: PropTypes.arrayOf(PropTypes.shape({})),
    marcasDeVehiculos: PropTypes.arrayOf(PropTypes.shape({})),
    clasesDeVehiculos: PropTypes.arrayOf(PropTypes.shape({})),
    serviciosDelVehiculo: PropTypes.arrayOf(PropTypes.shape({})),
    estadosDelVehiculo: PropTypes.arrayOf(PropTypes.shape({})),
    tiposDeEmpresa: PropTypes.arrayOf(PropTypes.shape({})),
    estados: PropTypes.arrayOf(PropTypes.shape({})),
    tiposDePersonas: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      descripcion: PropTypes.string,
    })),
  }).isRequired,
  disabled: PropTypes.bool,
  visibleBusquedaAvanzada: PropTypes.bool,
  visibleBusquedaAvanzadaCiudadano: PropTypes.bool,
  visibleBusquedaAvanzadaContribuyente: PropTypes.bool,
  setVisibleBusquedaAvanzada: PropTypes.func.isRequired,
  setVisibleBusquedaAvanzadaCiudadano: PropTypes.func.isRequired,
  setVisibleBusquedaAvanzadaContribuyente: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setEmpresaFound: PropTypes.func.isRequired,
  setCiudadanoFound: PropTypes.func.isRequired,
  setContribuyenteFound: PropTypes.func.isRequired,
  empresaFound: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  ciudadanoFound: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  contribuyenteFound: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  gpm: PropTypes.bool,
  allowAddAddress: PropTypes.bool,
  datosContactoOpcionales: PropTypes.bool,
};

FormsAddBusquedaAvanzada.defaultProps = {
  disabled: false,
  visibleBusquedaAvanzada: false,
  visibleBusquedaAvanzadaCiudadano: false,
  visibleBusquedaAvanzadaContribuyente: false,
  // isBajaDeOtroEstado: false,
  gpm: false,
  allowAddAddress: true,
  datosContactoOpcionales: false,
};

export default FormsAddBusquedaAvanzada;
